import { useMedicalChronology } from "@/components/MedicalChronology/contexts/MedicalChronologyContext";
import { useMedicalChronologySearch } from "@/components/MedicalChronology/contexts/MedicalChronologySearchContext";
import { useFocusSearchOnCmdF } from "@/hooks/ui/useFocusSearchOnCmdF";
import { useCaseSummary } from "@/hooks/useCaseSummary";
import type { MedChronEditResult } from "@/services/medChronServiceV2.types";
import { getErrorMessage } from "@/utils/errorUtils";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";

import { SummaryTabView } from "./SummaryTabView";
import { isBackgroundDraftDirty, isSummaryDraftDirty } from "./lib/summaryDraftUtils";

export const SummaryTab: React.FC = () => {
  const {
    caseId,
    selectedVersionId,
    citationPanel: { isOpen: isPdfPanelOpen },
    runVersionEdit,
    isFinishedCaseReadOnly,
  } = useMedicalChronology();
  const { searchQuery, setSearchQuery, debouncedSearchQuery } = useMedicalChronologySearch();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const medChronRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    medChronRef.current = document.querySelector<HTMLElement>(".medical-chronology");
  }, []);
  useFocusSearchOnCmdF(searchInputRef, medChronRef, { disabled: isPdfPanelOpen });

  const {
    summaryData,
    patientBackground,
    isLoading,
    patchSummary,
    patchPatientBackground,
    revalidate,
  } = useCaseSummary({
    caseId,
    versionId: selectedVersionId,
  });

  const initialSummaryDraft = useMemo(
    () => ({
      patient_name: summaryData?.patient_name ?? null,
      date_of_birth: summaryData?.date_of_birth ?? null,
      date_of_injury: summaryData?.date_of_injury ?? null,
      accident_description: summaryData?.accident_description ?? null,
      key_injuries: summaryData?.key_injuries ?? null,
      key_test_results: summaryData?.key_test_results ?? null,
      key_diagnoses: summaryData?.key_diagnoses ?? null,
    }),
    [summaryData]
  );

  const initialBackgroundDraft = useMemo(
    () => ({
      prior_medical_history: patientBackground?.prior_medical_history ?? null,
      pre_existing_conditions: patientBackground?.pre_existing_conditions ?? null,
      family_history: patientBackground?.family_history ?? null,
      social_history: patientBackground?.social_history ?? null,
      allergies_medications: patientBackground?.allergies_medications ?? null,
    }),
    [patientBackground]
  );

  const [summaryDraft, setSummaryDraft] = useState(initialSummaryDraft);
  const [backgroundDraft, setBackgroundDraft] = useState(initialBackgroundDraft);

  useEffect(() => setSummaryDraft(initialSummaryDraft), [initialSummaryDraft]);
  useEffect(() => setBackgroundDraft(initialBackgroundDraft), [initialBackgroundDraft]);

  const hasDraftChanges = useMemo(
    () =>
      isSummaryDraftDirty(summaryDraft, initialSummaryDraft) ||
      isBackgroundDraftDirty(backgroundDraft, initialBackgroundDraft),
    [summaryDraft, initialSummaryDraft, backgroundDraft, initialBackgroundDraft]
  );

  const handleSave = async () => {
    if (isSaving || !selectedVersionId) return;
    const summaryDirty = isSummaryDraftDirty(summaryDraft, initialSummaryDraft);
    const backgroundDirty = isBackgroundDraftDirty(backgroundDraft, initialBackgroundDraft);
    if (!summaryDirty && !backgroundDirty) return;

    // Sequential: on a finished case the first save forks, so the second must target the fork.
    const saveDirtyDrafts = async (): Promise<MedChronEditResult> => {
      const summaryResult = summaryDirty ? await patchSummary(summaryDraft) : null;
      if (!backgroundDirty && summaryResult) return summaryResult;
      const backgroundResult = await patchPatientBackground(
        backgroundDraft,
        summaryResult?.version_id ?? selectedVersionId
      );
      return {
        version_id: backgroundResult.version_id,
        forked: Boolean(summaryResult?.forked) || backgroundResult.forked,
      };
    };

    setIsSaving(true);
    try {
      await runVersionEdit(selectedVersionId, saveDirtyDrafts);
      const savedParts: string[] = [];
      if (summaryDirty) savedParts.push("case summary");
      if (backgroundDirty) savedParts.push("patient background");
      toast.success(
        savedParts.length === 2
          ? "Saved case summary and patient background"
          : `Saved ${savedParts[0]}`
      );
      setIsEditMode(false);
    } catch (e) {
      toast.error(getErrorMessage(e));
      await revalidate();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSummaryDraft(initialSummaryDraft);
    setBackgroundDraft(initialBackgroundDraft);
    setIsEditMode(false);
  };

  if (!selectedVersionId) {
    return null;
  }

  const isSummaryEditable = !isFinishedCaseReadOnly;

  return (
    <SummaryTabView
      isLoading={isLoading}
      isEditMode={isEditMode && isSummaryEditable}
      isEditModeAvailable={isSummaryEditable}
      isSaving={isSaving}
      hasDraftChanges={hasDraftChanges}
      summaryData={summaryData}
      summaryDraft={summaryDraft}
      backgroundDraft={backgroundDraft}
      setSummaryDraft={setSummaryDraft}
      setBackgroundDraft={setBackgroundDraft}
      onToggleEditMode={() => setIsEditMode((v) => !v)}
      onSave={handleSave}
      onCancel={handleCancel}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      debouncedSearchQuery={debouncedSearchQuery}
      searchInputRef={searchInputRef}
    />
  );
};
